import firebase from 'firebase/app';
import 'firebase/functions';

import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import Button from '../components/Button';
import Encouragement from '../components/Encouragement';
import ErrorBox from '../components/ErrorBox';
import Loader from '../components/Loader';
import RatingStars from '../components/RatingStars';

const ReviewWrapper = styled.div`
  text-align: left;
  padding: 1em 0;

  .talk {
    border: 1px solid black;
    padding: 1em;
    margin: 1em 0;
  }

  .submit-btn {
    font-size: 1em;
  }

  .skip {
    padding: 1em 0.7em;
    font-size: 0.9em;
    cursor: pointer;
    color: ${props => props.theme.colors.purple};
    text-decoration: underline;
  }

  table {
    margin: 2em 0;
    td:first-child { padding-right: 2em; }
    td {
      padding: 1em 0;
    }
  }
`;

class Review extends React.Component {
  constructor (props) {
    super(props);

    this.state = {
      reviewCount: 0,
      loading: false,
      review: {},
      submitting: false,
      talk: {},
    };
  }

  componentDidMount () {
    this.getTalk();
  }

  render () {
    return (
      <ReviewWrapper>
        <h1>Submission Review</h1>
        <p>
          We'll show you one redacted submission at a time. Please read each one thoroughly, then rate each one 1-5 on the following:
        </p>
        <ul>
          <li>The proposal itself (Is it clear and understandable?)</li>
          <li>Relevance to the Demuxed community (Will a room of developers working with video enjoy it?)</li>
          <li>Originality of the topic (Will this talk be new to the audience?)</li>
          <li>Plausible (Is it possible for this talk to be given in the time available?) <strong>note</strong>: this one is just a yes/no, but you can pretend it's 0 or 5 if it makes you feel better.</li>
        </ul>
        <p>
          Then, with those scores in mind, give this talk a rating out of 10! Finally, any notes that you can give to backup your ratings (whether they be positive or negative) will really go a long way to making your voice heard when we're creating the final schedule.
        </p>
        <p>
          If you review enough things, there is a 100% chance you're going to see the same topic multiple times. Please try your best to, for this round, not let this impact talks you see later. Pretend you're seeing it for the first time when you rate something for a metric like "originality".
        </p>
        <p><strong>Thank you for reviewing these talks!</strong></p>

        <Encouragement count={this.state.reviewCount} />

        {this.state.error &&
          <ErrorBox>
            <h3>We ran into a problem...</h3>
            <p><strong>Error message:</strong> "{this.state.error}"</p>
            <p>
              If this is an authentication issue, you may want to make sure you used the correct email. If it doesn't match what we have in the list of committee members, it won't work. You may want to try <Link to="/logout">signing out</Link> and using a different email if you think that might be the case.
            </p>
          </ErrorBox>}

        {!this.state.error &&
          <Loader loading={this.state.loading}>
            {!this.state.talk &&
              <div>
                <h2>No more talks!</h2>
                <p>
                  It looked like no more talks came back...That might mean you did it and successfully reviewed <em>every</em> talk!
                </p>
                <p>
                  ...Or I did something wrong. Want to try and <a className="skip" onClick={this.getTalk}>get another talk</a> just in case?
                </p>
              </div>}

            {this.state.talk &&
              <div>
                <div className="talk">
                  <h2>{this.state.talk.title}</h2>
                  <p>
                    {(this.state.talk.description || '').split('\n').map((item, key) =>
                      <span key={key}>{item}<br/></span>)}
                  </p>
                  <hr />
                  <p><strong>Preferred talk length(s):</strong> {(this.state.talk.lengths || []).join(', ')}</p>
                </div>

                <div className="review">
                  <table>
                    <tbody>
                      <tr>
                        <td>Clear and understandable</td>
                        <td>
                          <RatingStars
                            count={5}
                            value={this.state.review['Rating: Understandable']}
                            onChange={this.ratingChange('Rating: Understandable')} />
                        </td>
                      </tr>
                      <tr>
                        <td>Relevance</td>
                        <td>
                          <RatingStars
                            count={5}
                            value={this.state.review['Rating: Relevant']}
                            onChange={this.ratingChange('Rating: Relevant')}
                            half={false} />
                        </td>
                      </tr>
                      <tr>
                        <td>Originality</td>
                        <td>
                          <RatingStars
                            count={5}
                            value={this.state.review['Rating: Originality']}
                            onChange={this.ratingChange('Rating: Originality')} />
                        </td>
                      </tr>
                      <tr>
                        <td>Plausible</td>
                        <td>
                          <input
                            type="checkbox"
                            name="Plausible"
                            checked={this.state.review['Plausible'] || false}
                            onChange={e => this.ratingChange('Plausible')(e.target.checked)} />
                        </td>
                      </tr>
                      <tr>
                        <td>Overall Rating</td>
                        <td>
                          <RatingStars
                            count={10}
                            value={this.state.review['Rating: Overall']}
                            onChange={this.ratingChange('Rating: Overall')} />
                        </td>
                      </tr>

                      <tr>
                        <td>Notes</td>
                        <td>
                          <textarea
                            rows="5"
                            cols="50"
                            onChange={(e) => this.ratingChange('Additional Notes')(e.target.value)}>
                            {this.state.review['Additional Notes']}
                          </textarea>
                        </td>
                      </tr>

                      <tr>
                        <td>
                          <Button className="submit-btn" onClick={this.submit} disabled={this.state.submitting}>
                            {this.state.submitting
                              ? 'Submitting Review...'
                              : 'Submit'}
                          </Button>
                          <a className="skip" onClick={this.getTalk}>skip</a>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>}
          </Loader>}
      </ReviewWrapper>
    );
  }

  getTalk = () => {
    this.setState({ loading: true, review: {} });
    return firebase.functions().httpsCallable('randomTalk')()
      .then(({data: talk}) => this.setState({ talk, review: { 'Talk ID': [talk.id] } }))
      .then(() => this.setState({ loading: false }))
      .catch(err => this.setState({ loading: false, error: err.message }));
  }

  ratingChange = type => num => this.setState({ review: { ...this.state.review, [type]: num } });

  submit = () => {
    this.setState({ submitting: true });
    return firebase.functions().httpsCallable('submitReview')(this.state.review)
      .then(review =>
        this.setState({
          review: {},
          submitting: false,
          submittedReview: review,
          reviewCount: this.state.reviewCount + 1,
        }))
      .then(this.getTalk);
  };
};

export default Review;



// WEBPACK FOOTER //
// ./src/pages/Review.jsx